// noinspection GraphQLUnresolvedReference

import { useMutation } from '@tanstack/react-query'

import type { GraphqlClientOptionsProps } from '../../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../../base/api/graphqlClient.ts'
import type { MutationOpts } from '../../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../../utils/gql.ts'
import { impresoraFragmentAst } from '../fragments/impresoraFragment.ts'
import { ApiImpresoraInputProps, ImpresoraProps } from '../interfaces/impresora.ts'

const query = /* GraphQL */ gqlAst`
  ${impresoraFragmentAst}

  mutation IMPRESORA_ACTUALIZAR($id: ID!, $input: ImpresoraInput!) {
    impresoraActualizar(id: $id, input: $input) {
      ...impresoraFields
    }
  }
`

export const MUTATION_KEY_IMPRESORA_ACTUALIZAR = ['impresora-actualizar'] as const

export interface ApiImpresoraActualizarParams {
  id: string
  input: ApiImpresoraInputProps
}

/**
 * Servicio para actualizar una impresora.
 * @param params Id de la impresora y datos a actualizar.
 * @param options Headers adicionales y señal de cancelación de la petición.
 * @author isi-template
 */
export const apiImpresoraActualizar = async (
  params: ApiImpresoraActualizarParams,
  options: GraphqlClientOptionsProps = {},
): Promise<ImpresoraProps> => {
  try {
    const data = await graphqlClient.request<{ impresoraActualizar: ImpresoraProps }>({
      document: query,
      variables: { ...params },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.impresoraActualizar
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Hook de mutación para actualizar una impresora.
 * Para refrescar al terminar:
 * `onSuccess: (impresora) => Promise.all([invalidateImpresoraListado(), invalidateImpresoraPorId(impresora._id)])`.
 * @param options Opciones de React Query (onSuccess, onError, ...).
 * @author isi-template
 */
export const useImpresoraActualizar = (
  options?: MutationOpts<ImpresoraProps, ApiImpresoraActualizarParams>,
) => {
  return useMutation({
    mutationKey: MUTATION_KEY_IMPRESORA_ACTUALIZAR,
    ...options,
    mutationFn: (params: ApiImpresoraActualizarParams) => apiImpresoraActualizar(params),
  })
}
